import { z } from "zod";
import { IAccess } from "./access";
import { EProductSubType, EProductType } from "../constants/product.constants";

// Core product value schema used across product components
export const productItemValueSchema = z.object({
  title: z.string(),
  variantTitle: z.string(),
  price: z.union([z.number(), z.string()]),
  description: z.string().optional(),
  slotDuration: z.union([z.number(), z.string()]).optional(),
  walksPerDay: z.union([z.number(), z.string()]).optional(),
  sessionsPerMonth: z.union([z.number(), z.string()]).optional(),
  pricePerSession: z.union([z.number(), z.string()]).optional(),
});

export type ProductItemValue = z.infer<typeof productItemValueSchema>;

export interface ProductItemProps {
  value: ProductItemValue;
  onChange: (value: ProductItemValue) => void;
  isWalking: boolean;
  isGrooming: boolean;
}

export interface ProductModalProps {
  open: boolean;
  onClose: () => void;
  initialValue: ProductItemValue;
  isWalking: boolean;
  isGrooming: boolean;
  onSave: (value: ProductItemValue) => void;
  access?: IAccess;
}

export interface ProductListProps {
  values: ProductItemValue[];
  onChange: (values: ProductItemValue[]) => void;
  isWalking: boolean;
  isGrooming: boolean;
  label?: string;
  disabled?: boolean;
  onClick?: (fieldName: string) => void;
  access?: IAccess;
}

// Lightweight product/variant shapes used in migration UI
export type VariantLite = {
  id: string;
  productId: string;
  title: string;
  price: number;
  metadata?: {
    slotDuration?: number;
    walksPerDay?: number;
    numberOfSessionsPerMonth?: number;
    perSessionPrice?: number;
  };
};

export type ProductLite = {
  id: string;
  title: string;
  variants: VariantLite[];
};

/**
 * Product Variant Response Zod Schema
 */
export const productVariantResponseSchema = z.object({
  id: z.string(),
  productId: z.string(),
  title: z.string(),
  price: z.number(),
  position: z.number(),
  compareAtPrice: z.number().optional(),
  code: z.string().optional(),
  createdAt: z.number().optional(),
  updatedAt: z.number().optional(),
  imageId: z.number().nullable().optional(),
  metadata: z.record(z.unknown()).optional(),
});

export type ProductVariantResponseDto = z.infer<typeof productVariantResponseSchema>;

/**
 * Product Option Response Zod Schema
 */
export const productOptionResponseSchema = z.object({
  id: z.string(),
  productId: z.string(),
  name: z.string(),
  position: z.number(),
  values: z.array(z.string()),
});

export type ProductOptionResponseDto = z.infer<typeof productOptionResponseSchema>;

/**
 * Product Image Response Zod Schema
 */
export const productImageResponseSchema = z.object({
  id: z.number(),
  alt: z.string().optional(),
  position: z.number(),
  productId: z.string(),
  createdAt: z.number(),
  updatedAt: z.number(),
  width: z.number(),
  height: z.number(),
  src: z.string(),
  variantIds: z.array(z.number()),
});

export type ProductImageResponseDto = z.infer<typeof productImageResponseSchema>;

/**
 * Product Details Response Zod Schema
 */
export const productSingleVariantResponseSchema = z.object({
  id: z.string(),
  title: z.string(),
  type: z.nativeEnum(EProductType),
  subType: z.nativeEnum(EProductSubType),
  code: z.string().optional(),
  price: z.number().optional(),
  compareAtPrice: z.number().optional(),
  createdAt: z.number(),
  updatedAt: z.number(),
  tags: z.array(z.string()).optional(),
  status: z.string(),
  variant: productVariantResponseSchema,
  options: z.array(productOptionResponseSchema),
  images: z.array(productImageResponseSchema),
  meta: z.record(z.unknown()).optional(),
});

/**
* Type definitions inferred from Zod schemas
*/
export type ProductSingleVariantResponseDto = z.infer<typeof productSingleVariantResponseSchema>;

// Based on product-details-response.dto.ts structure
export interface ProductVariantMetadata {
  slotDuration?: number;
  walksPerDay?: number;
  numberOfSessionsPerMonth?: number;
  perSessionPrice?: number;
  [key: string]: unknown;
}

export interface ProductVariant {
  id: string;
  productId: string;
  title: string;
  price: number;
  position: number;
  compareAtPrice?: number;
  code?: string;
  createdAt: number;
  updatedAt: number;
  imageId?: number;
  metadata?: ProductVariantMetadata;
}

export interface ProductImage {
  id: number;
  alt?: string;
  position: number;
  productId: string;
  createdAt: number;
  updatedAt: number;
  width: number;
  height: number;
  src: string;
  variantIds: number[];
}

export interface Product {
  id: string;
  title: string;
  type: EProductType;
  subType: EProductSubType;
  createdAt: number;
  updatedAt: number;
  tags?: string[];
  status: string;
  variants: ProductVariant[];
  images: ProductImage[];
  meta?: Record<string, unknown>;
}

export interface GetProductsParams {
  type?: EProductType;
  subType?: EProductSubType;
  cityCode?: string;
  limit?: number;
  page?: number;
}

// Zod schema for products API response
export const productsApiResponseSchema = z.object({
  data: z.array(z.object({
    id: z.string(),
    title: z.string(),
    type: z.nativeEnum(EProductType),
    subType: z.nativeEnum(EProductSubType),
    createdAt: z.number(),
    updatedAt: z.number(),
    tags: z.array(z.string()).optional(),
    status: z.string(),
    variants: z.array(z.object({
      id: z.string(),
      productId: z.string(),
      title: z.string(),
      price: z.number(),
      position: z.number(),
      compareAtPrice: z.number().optional(),
      code: z.string().optional(),
      createdAt: z.number(),
      updatedAt: z.number(),
      imageId: z.number().optional(),
      metadata: z.record(z.unknown()).optional(),
    })),
    images: z.array(z.object({
      id: z.number(),
      alt: z.string().optional(),
      position: z.number(),
      productId: z.string(),
      createdAt: z.number(),
      updatedAt: z.number(),
      width: z.number(),
      height: z.number(),
      src: z.string(),
      variantIds: z.array(z.number()),
    })),
    meta: z.record(z.unknown()).optional(),
  })),
  meta: z.object({
    totalCount: z.number(),
    pageSize: z.number(),
    currentPage: z.number(),
    totalPages: z.number(),
    hasNext: z.boolean().optional(),
    hasPrev: z.boolean().optional(),
  }),
});

export type ProductsApiResponse = z.infer<typeof productsApiResponseSchema>;

export const productVariantItemResponseSchema = z.object({
  productId: z.string(),
  variantId: z.string(),
  quantity: z.number().optional(),
});

export type ProductVariantItemResponseDto = z.infer<typeof productVariantItemResponseSchema>;
